import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Package } from "react-feather";
import { AppearanceSettings } from "../../types/brand";

interface ProductShowcaseProps {
  selectedProductIds: string[];
  appearanceSettings?: AppearanceSettings | Partial<AppearanceSettings>;
  className?: string;
}

export function ProductShowcase({
  selectedProductIds,
  appearanceSettings,
  className = "",
}: ProductShowcaseProps) {
  const [products, setProducts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Calculate how many images to show based on screen size
  const getImagesPerView = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) return 3; // lg: 3 images
      if (window.innerWidth >= 568) return 2; // md: 2 images
      return 1; // sm: 1 image
    }
    return 1; // default to 1 for SSR/mobile-first
  };

  const [imagesPerView, setImagesPerView] = useState(1); // Start with 1 for mobile-first
  const maxSlide = Math.max(0, products.length - imagesPerView);

  // Update images per view on window resize and initial load
  useEffect(() => {
    const updateImagesPerView = () => {
      const newImagesPerView = getImagesPerView();
      setImagesPerView(newImagesPerView);
    };

    // Set initial value
    updateImagesPerView();

    // Add resize listener
    window.addEventListener("resize", updateImagesPerView);
    return () => window.removeEventListener("resize", updateImagesPerView);
  }, []);

  // Reset current slide if it's beyond the new max
  useEffect(() => {
    if (currentSlide > maxSlide) {
      setCurrentSlide(maxSlide);
    }
  }, [imagesPerView, maxSlide, currentSlide]);

  // Get theme from appearance settings
  const theme = appearanceSettings?.theme || "TYPE_1";

  useEffect(() => {
    const fetchProducts = async () => {
      if (selectedProductIds.length === 0) {
        setProducts([]);
        setIsLoading(false);
        return;
      } else {
        setProducts(selectedProductIds);
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [selectedProductIds]);

  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-xl font-semibold text-foreground">
            Product Showcase
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-square bg-muted rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  // Theme-based layout rendering
  const renderLayout = () => {
    switch (theme) {
      case "TYPE_1": // Modern Minimal - Grid Layout
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((product) => (
              <div
                key={product}
                className="group relative bg-card rounded-xl shadow-sm border border-border overflow-hidden hover:shadow-md transition-all duration-300"
              >
                <div className="aspect-square relative">
                  <img
                    src={product}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder/no-image.jpg";
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case "TYPE_2": // Bold & Vibrant - Horizontal Row
        return (
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {products.map((product) => (
                <div
                  key={product}
                  className="flex-shrink-0 w-48 bg-card rounded-2xl shadow-lg border-2 border-border overflow-hidden hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <div className="aspect-square relative">
                    <img
                      src={product}
                      alt={product}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/placeholder/no-image.jpg";
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "TYPE_3": // Elegant Professional - Consistent Grid Layout
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product}
                className="bg-card rounded-lg shadow-md border border-border overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="aspect-square relative">
                  <img
                    src={product}
                    alt={product}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder/no-image.jpg";
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case "TYPE_4": // Creative Playful - Carousel
        return (
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${
                    currentSlide * (100 / imagesPerView)
                  }%)`,
                }}
              >
                {products.map((product, _) => (
                  <div
                    key={product}
                    className="flex-shrink-0 py-4 px-2"
                    style={{ width: `${100 / imagesPerView}%` }}
                  >
                    <div className="bg-card max-w-56 mx-auto rounded-2xl shadow-xl border-4 border-border overflow-hidden hover:shadow-2xl hover:scale-105 hover:-rotate-1 transition-all mx-auto duration-300">
                      <div className=" lg:aspect-square relative">
                        <img
                          src={product}
                          alt={product}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/placeholder/no-image.jpg";
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {products.length > imagesPerView && maxSlide > 0 && (
              <>
                <button
                  onClick={() =>
                    setCurrentSlide((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentSlide === 0}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
                    currentSlide === 0
                      ? "bg-muted cursor-not-allowed opacity-50"
                      : "bg-card hover:shadow-xl hover:scale-110"
                  }`}
                >
                  <ChevronLeft
                    className={`w-5 h-5 ${
                      currentSlide === 0 ? "text-muted-foreground" : "text-muted-foreground"
                    }`}
                  />
                </button>
                <button
                  onClick={() =>
                    setCurrentSlide((prev) => Math.min(maxSlide, prev + 1))
                  }
                  disabled={currentSlide >= maxSlide}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
                    currentSlide >= maxSlide
                      ? "bg-muted cursor-not-allowed opacity-50"
                      : "bg-card hover:shadow-xl hover:scale-110"
                  }`}
                >
                  <ChevronRight
                    className={`w-5 h-5 ${
                      currentSlide >= maxSlide
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              </>
            )}
          </div>
        );

      default:
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((product) => (
              <div
                key={product}
                className="group relative bg-card rounded-xl shadow-sm border border-border overflow-hidden hover:shadow-md transition-all duration-300"
              >
                <div className="aspect-square relative">
                  <img
                    src={product}
                    alt={product}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder/no-image.jpg";
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className={""}>
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-muted-foreground" />
        <h3 className="text-xl p-4  font-semibold text-foreground">
          Product Showcase
        </h3>
      </div>
      {renderLayout()}
    </div>
  );
}
